import { AdvancedContextualSegments } from '@openx/components-targeting/Targeting/AdvancedTargeting/AdvancedContextualSegments.tsx';
import { AdvancedTargeting } from '@openx/components-targeting/Targeting/AdvancedTargeting/AdvancedTargeting.tsx';
import { AppInventory } from '@openx/components-targeting/Targeting/AppInventory/AppInventory.tsx';
import { Audience } from '@openx/components-targeting/Targeting/Audience/Audience.tsx';
import { Category } from '@openx/components-targeting/Targeting/Category/Category.tsx';
import { ContentObject } from '@openx/components-targeting/Targeting/ContentObject/ContentObject.tsx';
import { ContextualSegmentsTargeting } from '@openx/components-targeting/Targeting/ContextualSegments/ContextualSegmentsTargeting.tsx';
import { useStandaloneContextualSegmentsEnabled } from '@openx/components-targeting/Targeting/ContextualSegments/featureFlags.ts';
import { Domains } from '@openx/components-targeting/Targeting/Domains/Domains.tsx';
import { FormatType } from '@openx/components-targeting/Targeting/FormatType/FormatType.tsx';
import { Geographic } from '@openx/components-targeting/Targeting/Geographic/Geographic.tsx';
import { InventoryAndContent } from '@openx/components-targeting/Targeting/InventoryAndContent/InventoryAndContent.tsx';
import { Metacategories } from '@openx/components-targeting/Targeting/Metacategories/Metacategories.tsx';
import { Retargeting } from '@openx/components-targeting/Targeting/Retargeting/Retargeting.tsx';
import { TechnologyAndDevices } from '@openx/components-targeting/Targeting/TechnologyAndDevices/TechnologyAndDevices.tsx';
import { Video } from '@openx/components-targeting/Targeting/Video/Video.tsx';
import { Viewability } from '@openx/components-targeting/Targeting/Viewability/Viewability.tsx';
import { ViewThroughRate } from '@openx/components-targeting/Targeting/ViewThroughRate/ViewThroughRate.tsx';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import { type TargetingConfig, TargetingFields } from '@openx/types/targeting/targeting.ts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomTargetingWithContextualSegmentsField } from '../../components/PackageForm/sections/CustomTargetingWithContextualSegmentsField.tsx';

export const useTargeting = (): TargetingConfig[] => {
  const { t } = useTranslation();
  const { useContentObjectOptionsConfig, isOa, useRetargetingSegmentsOptionsFetch } = useTargetingContext();
  const isStandaloneContextualSegmentsEnabled = useStandaloneContextualSegmentsEnabled();

  const conditional = useCallback((element: TargetingConfig, condition: boolean) => (condition ? [element] : []), []);

  return useMemo(() => {
    return [
      {
        TargetingComponent: Audience,
        field: TargetingFields.AUDIENCE,
        name: t('Audience'),
      },
      ...conditional(
        {
          TargetingComponent: ContextualSegmentsTargeting,
          field: TargetingFields.CONTEXTUAL_SEGMENTS,
          name: t('Contextual Segments'),
        },
        !!isOa && isStandaloneContextualSegmentsEnabled
      ),
      {
        TargetingComponent: InventoryAndContent,
        field: TargetingFields.INVENTORY_AND_CONTENT,
        name: t('Inventory'),
      },
      {
        TargetingComponent: Domains,
        field: TargetingFields.URL_TARGETING,
        name: t('Domains'),
      },
      {
        TargetingComponent: AppInventory,
        field: TargetingFields.APP_INVENTORY,
        name: t('App Inventory'),
      },
      {
        TargetingComponent: Geographic,
        field: TargetingFields.GEOGRAPHIC,
        name: t('Current Location'),
      },
      {
        TargetingComponent: TechnologyAndDevices,
        field: TargetingFields.TECHNOLOGY_AND_DEVICES,
        name: t('Environment'),
      },
      {
        TargetingComponent: Category,
        field: TargetingFields.CATEGORY,
        name: t('IAB Categories'),
      },
      {
        TargetingComponent: Metacategories,
        field: TargetingFields.METACATEGORY,
        name: t('OpenX Categories and Keywords'),
      },
      {
        TargetingComponent: FormatType,
        field: TargetingFields.FORMAT_TYPE,
        name: t('Distribution Channel'),
      },
      {
        TargetingComponent: Viewability,
        field: TargetingFields.VIEWABILITY,
        name: t('Viewability'),
      },
      {
        TargetingComponent: Video,
        field: TargetingFields.VIDEO,
        name: t('Video Attributes'),
      },
      {
        TargetingComponent: ViewThroughRate,
        field: TargetingFields.VIEW_THROUGH_RATE,
        name: t('View Through Rate'),
      },
      ...conditional(
        {
          TargetingComponent: Retargeting,
          field: TargetingFields.RETARGETING,
          name: t('Retargeting'),
        },
        !!useRetargetingSegmentsOptionsFetch
      ),
      ...conditional(
        {
          TargetingComponent: ContentObject,
          field: TargetingFields.CONTENT_OBJECT,
          name: t('CTV Content Object'),
        },
        !!useContentObjectOptionsConfig
      ),
      // With the standalone flag, Contextual Segments get their own card: V3 in OA (placed after Audience above),
      // V1 elsewhere. Without it, they are a section of the Custom Targeting card (V2 in OA, V1 elsewhere).
      ...(isStandaloneContextualSegmentsEnabled
        ? [
            ...conditional(
              {
                TargetingComponent: AdvancedContextualSegments,
                field: TargetingFields.CONTEXTUAL_SEGMENTS,
                name: t('Contextual Segments'),
              },
              !isOa
            ),
            {
              TargetingComponent: AdvancedTargeting,
              field: TargetingFields.CUSTOM_VARIABLES,
              name: t('Custom Targeting'),
            },
          ]
        : [
            {
              TargetingComponent: CustomTargetingWithContextualSegmentsField,
              field: TargetingFields.CUSTOM_VARIABLES,
              name: t('Custom Targeting'),
            },
          ]),
    ];
  }, [
    t,
    conditional,
    useContentObjectOptionsConfig,
    useRetargetingSegmentsOptionsFetch,
    isOa,
    isStandaloneContextualSegmentsEnabled,
  ]);
};
